import equipmentAtom from '@/pages/Equipment/atoms/equipment.atom'
import equipmentSelectors from '@/pages/Equipment/equipment.selectors'
import usePersistentState from '@/shared/hooks/usePersistentState'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import deviceMapFiltersAtom, { IDeviceMapFiltersState } from '../../deviceMapFilters.atom'
import Map, { TMapBounds } from '../../../../shared/Map'
import MapEquipmentFilter from '../../utils/mapEquipmentFilter'
import { IEquipment } from '@/pages/Equipment/equipment.types'
import searchUtils, { SearchResult } from '@/utils/search'
import equipmentUtils from '@/pages/Equipment/equipment.utils'
import css from '../../deviceMap.module.css'
import StatisticsCard from '../StatisticsCard'
import EquipmentFilterModels from '../EquipmentFilterModels'
import EquipmentFilterStatus from '../EquipmentFilterStatus'
import SearchInput from '@/shared/Inputs/SearchInput'
import ShortEquipmentCard from '@/pages/Equipment/components/Common/ShortEquipmentCard'
import urls from '@/navigation/urls'

const DeviceMapContent = () => {
  const navigate = useNavigate()
  const equipment = useRecoilValue(equipmentAtom).items
  const equipmentCountByStatus = useRecoilValue(equipmentSelectors.selectEquipmentCountByStatus)
  const equipmentCountByFlag = useRecoilValue(equipmentSelectors.selectEquipmentCountByFlag)

  const [mapBounds, setMapBounds] = usePersistentState<TMapBounds>(null, 'map/mapBounds', 'localstorage')
  const [filtersState, setFiltersState] = useRecoilState(deviceMapFiltersAtom)

  const equipmentFilter = useMemo(() => new MapEquipmentFilter(equipment), [equipment])
  const filteredEquipment = useMemo(() => {
    let resultEquipment = equipment

    if (mapBounds) {
      resultEquipment = equipmentFilter.getFittedIntoBounds(mapBounds) as IEquipment[]
    }
    if (filtersState.statuses.length > 0) {
      resultEquipment = resultEquipment.filter((item) => filtersState.statuses.includes(item.state))
    }
    if (filtersState.models.length > 0) {
      resultEquipment = resultEquipment.filter((item) => filtersState.models.includes(item.modelId))
    }

    return resultEquipment
  }, [mapBounds, equipmentFilter, filtersState.statuses, filtersState.models])

  const equipmentSearchResults = useMemo(() => {
    if (!filtersState.search) return filteredEquipment.map((e) => ({ equipment: e, searchResults: [] }))
    const searchParts = searchUtils.prepareSearchString(filtersState.search)

    const matches: { searchResults: SearchResult[]; equipment: IEquipment }[] = []
    for (const e of filteredEquipment) {
      if (!e.location) continue
      const result = searchUtils.search(e.name + ' ' + equipmentUtils.getEquipmentAddress(e.location), searchParts)
      if (!result.length) continue

      matches.push({ equipment: e, searchResults: result })
    }
    return matches
  }, [filtersState.search, filteredEquipment])

  const handleFilterChange = <T extends keyof IDeviceMapFiltersState>(value: IDeviceMapFiltersState[T], key: T) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className={css.wrapper}>
      <div className={css.statisticsWrapper}>
        <StatisticsCard
          heading="Активны"
          style={{ minWidth: '140px' }}
          tooltipText="Все лифты, подключенные к системе и отправляющие телеметрию в настоящий момент."
        >
          <p className="text-nm">
            <span className="text-success">{equipmentCountByStatus['RUNNING']}</span>
          </p>
        </StatisticsCard>
        <StatisticsCard
          heading="Отключены"
          style={{ minWidth: '140px' }}
          tooltipText="Лифты, которые были успешно подключены и отправляли телеметрию, но после этого отключились. Чаще всего это означает отсутствие интернета у станции, либо мануальное ее отключение."
        >
          <p className="text-nm">
            <span className="text-default text-warning">{equipmentCountByStatus['DISCONNECTED'] || '—'}</span>
          </p>
        </StatisticsCard>
        <StatisticsCard
          heading="Предупреждение"
          style={{ minWidth: '140px' }}
          tooltipText="Статус станции лифта. В этом случае лифт продолжает работать в нормальном режиме, однако требует внимания специалиста для устранения дефекта. С кодом предупрждения и его описанием можно ознакомиться на странице лифта."
        >
          <p className="text-nm">
            <span className="text-warning">{equipmentCountByFlag['WARNING'] || '—'}</span>
          </p>
        </StatisticsCard>
        <StatisticsCard
          heading="Авария"
          style={{ minWidth: '140px' }}
          tooltipText="Лифт не работает и требует внимания специалиста. Станция пытается автоматически устранить ошибку. Код и описание ошибки можно узнать на странице лифта."
        >
          <p className="text-nm">
            <span className={equipmentCountByFlag['ERROR'] ? 'text-error' : ''}>
              {equipmentCountByFlag['ERROR'] || '—'}
            </span>
          </p>
        </StatisticsCard>
        <StatisticsCard
          heading="Неустр. Авария"
          style={{ minWidth: '140px' }}
          tooltipText="Лифт не работает и требует внимания специалиста. Код и описание ошибки можно узнать на странице лифта."
        >
          <p className="text-nm">
            <span className={equipmentCountByFlag['FATAL_FAULT'] ? 'text-error' : ''}>
              {equipmentCountByFlag['FATAL_FAULT'] || '—'}
            </span>
          </p>
        </StatisticsCard>
      </div>
      <div className={css.filtersWrapper}>
        <EquipmentFilterModels models={filtersState.models} name="models" onChange={handleFilterChange} />
        <EquipmentFilterStatus statuses={filtersState.statuses} name="statuses" onChange={handleFilterChange} />
      </div>
      <Map onResize={setMapBounds} mapBounds={mapBounds} equipment={equipmentSearchResults.map((c) => c.equipment)} />
      <div className={`flex-lines gap16 ${css.devicesList}`}>
        <div style={{ paddingRight: '24px' }}>
          <SearchInput
            value={filtersState.search}
            name="search"
            onChange={handleFilterChange}
            size="large"
            placeHolder="Поиск лифтов и адресов"
          />
        </div>
        <div className="flex-lines gap16" style={{ minHeight: 0, overflow: 'auto', paddingRight: '24px' }}>
          {equipmentSearchResults.map((data) => (
            <ShortEquipmentCard
              equipment={data.equipment}
              key={data.equipment.id}
              onClick={() => navigate(urls.liftScreen(data.equipment.id))}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default DeviceMapContent
